import { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import { ArrowRight, Crown } from "lucide-react";
import api from "../lib/api";
import { imgProps } from "../lib/img";
import OffersStrip from "../components/OffersStrip";
import { usePageSeo, orgJsonLd } from "../lib/seo";
import { restaurantJsonLd, NAP, HOME_COPY, CANON } from "../data/nap";
import { GuestReviews } from "../components/GuestReviews";
import { HomeFaq, homeFaqJsonLd } from "../components/HomeFaq";
import { NapBlock } from "../components/NapBlock";
import { OrderCtas } from "../components/OrderCtas";
import { FactsBlock } from "../components/FactsBlock";
import { ThreeWays } from "../components/ThreeWays";
import { AnnouncementBar } from "../components/AnnouncementBar";
import { useCart } from "../context/CartContext";
import { Editable } from "../components/edit/Editable";
import { EditableImage } from "../components/edit/EditableImage";
import { EditEntityButton } from "../components/edit/EntityEditor";
import { useContent } from "../context/ContentContext";
import { Page, PageHero, Section, Card, Button } from "../components/layout/Page";
import { T } from "../theme/tokens";

function SignatureDishes() {
  const [dishes, setDishes] = useState([]);
  useEffect(() => { api.get("/dishes").then(({ data }) => setDishes(data.filter((d) => d.slug && d.name && d.price > 0))).catch(() => {}); }, []);
  if (!dishes.length) return null;
  return (
    <Section title={<Editable k="home.signature.heading" fallback="Signature dishes" label="Signature dishes heading" />} testId="signature-dishes">
      <div className="flex gap-5 overflow-x-auto pb-3 -mx-1 px-1">
        {dishes.map((d) => (
          <Card key={d.slug} as={Link} to={`/menu/${d.slug}`} data-testid={`signature-dish-${d.slug}`} className="w-52 shrink-0 overflow-hidden !p-0 block">
            {d.image ? <img {...imgProps(d.image, [320, 480], "208px")} alt={d.name} width="208" height="140" className="w-full h-36 object-cover" loading="lazy" />
              : <div className="w-full h-36 flex items-center justify-center text-center px-3 text-sm font-bold" style={{ background: "var(--ut-panel)", color: "var(--ut-gold)" }}>{d.name}</div>}
            <div className="p-4">
              <p className="font-semibold text-sm leading-tight">{d.name}</p>
              <p className="text-xs mt-1" style={{ color: "var(--ut-muted)" }}>${d.price.toFixed(2)}</p>
            </div>
          </Card>
        ))}
      </div>
    </Section>
  );
}

const HERO_BANNERS = [
  { kicker: "FROM THE BHATTI", headline: "Butter Chicken,\nthe Dhaba Way.", sub: "Smoky. Buttery. Never sweet.", img: "/menu-photos/butter-chicken.webp", alt: "Dhaba Butter Chicken", cta: "Order Urban Turban", item: "tb_dhaba_butter_chicken" },
  { kicker: "SEALED · STEAMED · DUM", headline: "Biryani Opened\nWhen You Order.", sub: "Saffron rice, whole spices, real dum.", img: "/menu-photos/chicken-dum-biryani.webp", alt: "Chicken Dum Biryani", cta: "Get the Biryani", item: "tb_chicken_dum_biryani" },
  { kicker: "FEEDS THE WHOLE TABLE", headline: "The Grill\nof Punjab.", sub: "Tikka, seekh & tandoori — one platter.", img: "/menu-photos/grill-of-punjab-punjabi-mixed-grill-combo.webp", alt: "Punjabi Mixed Grill", cta: "Order the Grill", item: "tb_chicago_dhaba_mixed_grill" },
];

function HeroBanner() {
  const { editMode } = useContent();
  const [idx, setIdx] = useState(() => Math.floor(Math.random() * HERO_BANNERS.length));
  const b = HERO_BANNERS[idx];
  const K = (f) => `home.banner.${idx}.${f}`;
  return (
    <div className="relative w-full max-w-md mx-auto lg:ml-auto">
      <Link to="/store/restaurant" state={{ featuredId: b.item }} data-testid="hero-promo-banner" onClick={(e) => { if (editMode) e.preventDefault(); }}
        className={`${T.card} relative block w-full overflow-hidden group !p-0`}>
        <EditableImage k={K("img")} fallback={b.img} alt={b.alt} label={`Banner ${idx + 1} photo`} wrapperClassName="block aspect-[4/3]"
          width="480" height="360" loading="eager" fetchPriority="high" className="w-full h-full object-cover transition-transform duration-500 group-hover:scale-105" />
        <div className="p-6 [&_.edit-badge]:pointer-events-auto">
          <Editable as="p" k={K("kicker")} fallback={b.kicker} label={`Banner ${idx + 1} kicker`} className={T.eyebrow} />
          <Editable as="p" k={K("headline")} fallback={b.headline} label={`Banner ${idx + 1} headline`} multiline className="text-2xl sm:text-3xl mt-2 whitespace-pre-line" style={{ fontFamily: "var(--ut-font-display)", color: "var(--ut-ink)" }} />
          <Editable as="p" k={K("sub")} fallback={b.sub} label={`Banner ${idx + 1} subline`} className="text-sm mt-2" style={{ color: "var(--ut-muted)" }} />
          <span className={`${T.btnPrimary} mt-5`} data-testid="hero-promo-cta"><Editable k={K("cta")} fallback={b.cta} label={`Banner ${idx + 1} button`} /> <ArrowRight size={15} aria-hidden="true" /></span>
        </div>
      </Link>
      {editMode && (
        <div className="absolute -bottom-8 right-0 flex gap-1" data-testid="banner-switcher">
          {HERO_BANNERS.map((_, i) => <button key={i} type="button" onClick={() => setIdx(i)} className={`text-[10px] font-bold px-2 py-0.5 rounded-full ${i === idx ? "bg-black text-white" : "bg-neutral-200"}`}>Banner {i + 1}</button>)}
        </div>
      )}
    </div>
  );
}

export default function Home() {
  const { deliveryInfo } = useCart();
  const [businesses, setBusinesses] = useState([]);

  usePageSeo({ title: HOME_COPY.title, description: HOME_COPY.description, canonicalPath: "/", ogImage: "/og/urban-turban.jpg",
    jsonLd: [orgJsonLd(), restaurantJsonLd({ department: { "@id": `${CANON}/store/halwai#halwai` } }), homeFaqJsonLd(deliveryInfo)] });

  useEffect(() => { api.get("/businesses").then(({ data }) => setBusinesses(data)).catch(() => {}); }, []);

  const food = businesses.filter((b) => b.type === "food" && b.is_public);
  const jewelry = businesses.find((b) => b.type === "jewelry");

  return (
    <Page testId="home-page">
      <div className={`${T.container} pt-4`}><AnnouncementBar /></div>
      <PageHero eyebrow={<Editable k="home.eyebrow" fallback={HOME_COPY.eyebrow} label="Home eyebrow" testId="home-eyebrow" />}
        title={<Editable k="home.h1" fallback={HOME_COPY.h1} label="Home headline (H1)" testId="home-h1" />}
        lead={<span data-testid="home-facts-line">Urban Turban by VeerGi is an Indian restaurant serving Punjabi food at 2660 N Halsted St in Lincoln Park, Chicago, near DePaul. Open 2–11 PM, closed Tuesdays. Order dine-in, pickup or delivery at <Link to="/store/restaurant" className="underline underline-offset-2 font-semibold">tryurbanturban.com/store/restaurant</Link>, or call <a href="tel:+13129550331" className="underline underline-offset-2 font-semibold">(312) 955-0331</a>.</span>}
        aside={<HeroBanner />}>
        <Editable as="p" k="home.intro" fallback={HOME_COPY.intro} label="Home intro" multiline className="text-base font-medium mt-5 max-w-xl" testId="home-intro" style={{ color: "var(--ut-ink)" }} />
        <p className="text-sm font-bold tracking-[0.18em] uppercase mt-3" data-testid="home-subline" style={{ color: "var(--ut-ink)" }}>Indian · Punjabi · Pakistani</p>
        <div className="mt-8"><OrderCtas testPrefix="hero" showMusic /></div>
      </PageHero>

      <Section eyebrow="Order" title={<Editable k="home.h2" fallback={HOME_COPY.h2} label="Home subheading" testId="home-h2" />}>
        <ThreeWays />
      </Section>

      <Section title={<Editable k="home.offers.heading" fallback="Today's Offers" label="Offers heading" />} testId="home-offers-rail">
        <OffersStrip />
      </Section>

      <Section eyebrow="In their words" title="What guests say" testId="home-guest-reviews">
        <GuestReviews testPrefix="home" />
      </Section>

      <Section eyebrow="Our kitchens" title="Urban Turban and Urban Express">
        <div className="grid grid-cols-1 md:grid-cols-6 gap-5" data-testid="business-grid">
          {food.map((b, idx) => (
            <Link key={b.id} to={`/store/${b.slug}`} data-testid={`business-card-${b.slug}`}
              className={`${T.card} group relative overflow-hidden !p-0 ${idx === 0 ? "md:col-span-4" : "md:col-span-2"} h-72 md:h-96`}>
              <EditEntityButton type="business" entity={b} className="absolute top-3 right-3 z-10" label={`Edit ${b.name}`}
                onSaved={(nb) => setBusinesses((all) => all.map((x) => (x.id === nb.id ? nb : x)))} />
              {b.image && <img {...imgProps(b.image, [480, 800, 1200], idx === 0 ? "(min-width: 768px) 60vw, 100vw" : "(min-width: 768px) 33vw, 100vw")} alt={b.name} width="800" height="384" className="absolute inset-0 w-full h-full object-cover transition-transform duration-500 group-hover:scale-105" loading={idx === 0 ? "eager" : "lazy"} />}
              <div className="absolute inset-x-0 bottom-0 p-6" style={{ background: "linear-gradient(to top, rgba(43,33,23,0.88), rgba(43,33,23,0.25), transparent)" }}>
                <h3 className="text-2xl text-white" style={{ fontFamily: "var(--ut-font-display)" }}>{b.name}</h3>
                <p className="text-sm text-white/80 mt-1">{b.tagline}</p>
                <span className={`${T.btnPrimary} mt-4 !min-h-[38px] !px-4 text-xs`}>{b.ordering === "coming_soon" ? "Coming soon" : <>Order now <ArrowRight size={13} aria-hidden="true" /></>}</span>
              </div>
            </Link>
          ))}
          {jewelry && (
            <a href="https://regaljewels.com" target="_blank" rel="noopener noreferrer" data-testid="business-card-jewelry"
              className={`${T.card} group relative overflow-hidden !p-0 md:col-span-6 h-72`}>
              {jewelry.image && <img {...imgProps(jewelry.image, [800, 1200, 1600], "100vw")} alt={jewelry.name} width="1200" height="320" className="absolute inset-0 w-full h-full object-cover transition-transform duration-500 group-hover:scale-105" loading="lazy" />}
              <div className="absolute inset-0 p-8 sm:p-12 flex flex-col justify-center" style={{ background: "linear-gradient(to right, rgba(43,33,23,0.85), rgba(43,33,23,0.3), transparent)" }}>
                <p className={`${T.eyebrow} !text-[#E8B33A]`}>The collection</p>
                <h3 className="text-3xl sm:text-4xl text-white mt-2" style={{ fontFamily: "var(--ut-font-display)" }}>{jewelry.name}</h3>
                <p className="text-sm text-white/80 mt-2 max-w-xs">{jewelry.tagline}</p>
                <span className={`${T.btnGhost} mt-5 w-fit !text-white !border-white/70`}>Shop jewelry <ArrowRight size={13} aria-hidden="true" /></span>
              </div>
            </a>
          )}
        </div>
      </Section>

      <SignatureDishes />

      <Section>
        <p className="text-base" data-testid="home-halwai-link">
          <Link to="/store/halwai" className="underline font-semibold">Indian Punjabi mithai boxes (1/2 lb) at Urban Halwai</Link>
        </p>
      </Section>

      <Section band>
        <Link to="/club" data-testid="home-club-banner" className="flex flex-col sm:flex-row sm:items-center justify-between gap-5">
          <div className="flex items-center gap-4">
            <Crown size={28} style={{ color: "var(--ut-gold)" }} className="shrink-0" aria-hidden="true" />
            <div>
              <Editable as="p" k="home.club.title" fallback="Urban Club — $79/year" label="Club banner title" className="text-2xl" style={{ fontFamily: "var(--ut-font-display)" }} />
              <p className="text-sm mt-1" style={{ color: "var(--ut-muted)" }}>{deliveryInfo?.club_free_delivery_over ? `Free delivery over $${deliveryInfo.club_free_delivery_over} · ` : ""}<Editable k="home.club.sub" fallback="a free item every day · 5% back on pickup & dine-in" label="Club banner text" /></p>
            </div>
          </div>
          <span className={`${T.btnPrimary} shrink-0 w-fit`}><Editable k="home.club.cta" fallback="Join now" label="Club button" /></span>
        </Link>
      </Section>

      <Section eyebrow="Good to know" title="Catering, dietary options and how to find us" testId="home-nap-section">
        <div className="grid md:grid-cols-3 gap-5 items-start">
          <Card>
            <Editable as="p" k="home.catering.kicker" fallback="Catering" label="Catering kicker" className={T.eyebrow} />
            <p className="text-base mt-3 leading-relaxed" data-testid="home-catering"><Editable k="home.catering.text" fallback="From corporate lunches to family celebrations, we bring the full Urban Turban spread to you. Call" label="Catering text" multiline /> <a href={NAP.cateringHref} className="font-bold underline"><Editable k="nap.catering" fallback="312-955-0463" label="Catering phone" /></a> <Editable k="home.catering.tail" fallback="to start planning." label="Catering text (after phone)" /></p>
          </Card>
          <Card>
            <Editable as="p" k="home.dietary.kicker" fallback="Halal · Vegan · Vegetarian" label="Dietary kicker" className={T.eyebrow} />
            <Editable as="p" k="home.dietary.text" fallback="Halal, Vegan, and Vegetarian - Every guest is welcome at this table. Our menu is built so no one has to compromise." label="Dietary text" multiline className="text-base mt-3 leading-relaxed" testId="home-dietary" />
            <Button to="/page/dietary-options" variant="ghost" className="mt-4"><Editable k="home.dietary.link" fallback="Dietary options" label="Dietary link text" /></Button>
          </Card>
          <NapBlock />
        </div>
      </Section>

      <Section narrow testId="home-facts">
        <FactsBlock />
        <p className="text-sm mt-4" style={{ color: "var(--ut-muted)" }}>More answers on our <Link to="/faq" className="font-semibold underline" data-testid="home-faq-link">FAQ page</Link>.</p>
      </Section>
      <Section>
        <HomeFaq deliveryInfo={deliveryInfo} />
      </Section>
    </Page>
  );
}
